import {type FC, type ReactNode, useCallback, useEffect, useLayoutEffect, useRef} from 'react';
import {useEditor, EditorContent} from '@tiptap/react';
import type {Editor as IEditor} from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

import classNames from 'classnames';

import {Extension, extensions} from '@tiptap/core';

import {TimeEditorHashtagMentionExtension} from 'features/hashtags/extension';

import {urlRegex} from 'utils/paste';

import {getSelectedText} from 'utils/utils';

import {Slice, Fragment, DOMParser, type Node as ProsemirrowNode} from 'prosemirror-model';

import {useAppSelector} from 'stores/redux_store';
import {hasFormattingBar} from 'features/editor/selectors/has_formatting_bar';
import {sendToStatist} from 'packages/statist';

import type {Features, Props} from './types';
import {EditorContext, useEditorContext} from './context';
import {
    TimeWebkitEnhancedFormatMarksExtension,
    TimeWebkitBlockquoteExtension,
    TimeWebkitCommandsExtension,
    TimeWebkitHardBreakExtension,
    TimeWebkitHeadingExtension,
    TimeWebkitListItemExtension,
    TimeWebkitParagraphExtension,
    TimeWebkitTextExtension,
    TimeWebkitBoldExtension,
    TimeWebkitItalicExtension,
    TimeWebkitStrikeExtension,
} from './extensions/editor-custom-extensions';
import editorStyles from './editor.module.css';
import {FormattingBar} from './components/formatting-bar';
import {ActionsBar} from './components/actions-bar';
import {TimeEditorMentionExtension} from './extensions/mention';
import {TimeWebkitOrderedListExtension} from './extensions/ordered-list';

import {keymap} from './keydownHandler';
import {TimeWebappEditorKeymapExtension} from './extensions/keymap';

import {TimeWebkitMarkdownExtension, parseHtmlFromMarkdown} from './markdown-extension';
import {TimeEditorChannelMentionExtension} from './extensions/channel';
import {TimeEditorSlashCommandExtension} from './extensions/slash-command';
import {mapSlice} from './utils';
import {TimeWebkitEditorPlaceholderPlugin} from './extensions/placeholder';
import {TimeWebkitCodeBlockExtension} from './extensions/code-block';
import {TimeWebkitEmojiExtension} from './extensions/emoji-search';
import {TimeWebkitLinkExtension} from './extensions/link/link';
import {TimeWebkitCodeExtension} from './extensions/code/code';
import {LimitCounter} from './components/limit-counter';
import {MentionMark} from './extensions/mention/mention-mark';
import {toMarkdown} from './to_markdown';
import {TimeWebkitBulletListExtension} from './extensions/bullet-list/bullet-list-extension';
import {TimeWebkitTypographyExtension} from './extensions/typography/typography-extension';
import {TimeWebkitHorizontalRuleExtension} from './extensions/horizontal-rule/horizontal-rule-extension';

const Content: FC<{
    bodySlot?: ReactNode;
    actionsSlot?: ReactNode;
    emojiPicker?: ReactNode;
    fileUploader?: ReactNode;
}> = (props) => {
    const {bodySlot, actionsSlot, ...actionsBarItems} = props;
    const {editor, maxCharacters, charactersCount} = useEditorContext();
    const isFormattingBarOpened = useAppSelector(hasFormattingBar);

    const containerRef = useRef<HTMLDivElement>(null);

    const handleFocusReturn = useCallback((event: MouseEvent) => {
        if (!containerRef?.current?.contains(event.target as Node)) {
            return;
        }

        editor?.chain().focus().run();
    }, [containerRef, editor]);

    return (
        <div
            ref={containerRef}
            className={classNames(editorStyles.editorWrapper, {[editorStyles.withFormattingBar]: isFormattingBarOpened})}
        >
            {isFormattingBarOpened && (
                <FormattingBar
                    outerRef={containerRef}
                    onClosePopover={handleFocusReturn}
                />
            )}
            {editor ? (
                <div className={editorStyles.limitCounterWrapper}>
                    <EditorContent
                        className={classNames(
                            editorStyles.editorContent,
                        )}
                        editor={editor}
                    />
                    <LimitCounter
                        max={maxCharacters}
                        current={charactersCount}
                        className={editorStyles.limitCounter}
                    />
                </div>
            ) : null}
            <div className={editorStyles.bodySlot}>{bodySlot}</div>
            <ActionsBar
                onClosePopover={handleFocusReturn}
                {...actionsBarItems}
            >
                {actionsSlot}
            </ActionsBar>
        </div>
    );
};

function getNodesFromHtmlString(html: string) {
    const innerHtml = html.trim();
    if (!innerHtml) {
        return null;
    }

    const template = document.createElement('template');
    template.innerHTML = innerHtml;
    const result = template.content.children;

    return result;
}

const coreExtensions = [
    extensions.ClipboardTextSerializer,
    extensions.Commands,
    extensions.Editable,
    extensions.FocusEvents,
    extensions.Tabindex,
];

const EMPTY_ARRAY: any[] = [];

declare global {
    interface Window {
        __EDITOR__: IEditor | null;
    }
}

const disableMentionsInExtensions = new Set([
    TimeWebkitCodeBlockExtension.name,
]);
const disableMentionsInMarks = new Set(['code']);

export const Editor = (props: Props) => {
    const {
        className,
        editable,
        features = {},
        onEnterPress,
        onArrowUpPress,
        onReplyLastPost,
        onReactLastPost,
        channelId,
        teamId,
        ctrlSend,
        codeBlockOnCtrlEnter,
        charactersCount,
        maxCharacters,
        placeholder,
        predefinedMentions,
        predefinedChannels = EMPTY_ARRAY,
        predefinedHashtags = EMPTY_ARRAY,
        rootId,
        setEditor,
        fileAttachmentSlot,
        sendMenuSlot,
        emojiPicker,
        fileUploader,
        onContentChange,
        onCaretPositionChange,
        draftMessage,
        onEscPress,
        withinThread = false,
        withinDirect = false,
        useCustomGroupMentions,
        useLDAPGroupMentions,
        setServerError,
    } = props;

    const {codeBlock, emoji, mention} = features as Features;

    const hasMentions = mention?.isActive;

    const editorExtensions = [
        ...coreExtensions,
        TimeWebappEditorKeymapExtension.configure({ctrlSend, codeBlockOnCtrlEnter}),
        StarterKit.configure({
            heading: false,
            paragraph: false,
            blockquote: false,
            orderedList: false,
            bulletList: false,
            hardBreak: false,
            listItem: false,
            horizontalRule: false,
            text: false,
            codeBlock: false,
            bold: false,
            italic: false,
            strike: false,
            code: false,
        }),
        TimeWebkitTextExtension,
        TimeWebkitBoldExtension,
        TimeWebkitItalicExtension,
        TimeWebkitStrikeExtension,
        TimeWebkitCodeExtension,
        TimeWebkitHeadingExtension,
        TimeWebkitParagraphExtension,
        TimeWebkitCodeBlockExtension.configure({ctrlSend: ctrlSend || codeBlockOnCtrlEnter}),
        TimeWebkitBlockquoteExtension.configure({
            ctrlSend,
        }),
        TimeWebkitOrderedListExtension,
        TimeWebkitBulletListExtension,
        TimeWebkitLinkExtension,
        TimeWebkitListItemExtension.configure({
            ctrlSend,
        }),
        TimeWebkitHorizontalRuleExtension,
        TimeWebkitEnhancedFormatMarksExtension,
        TimeWebkitTypographyExtension,
        Extension.create({
            addProseMirrorPlugins() {
                return [
                    keymap({
                        'Mod-b': () => {
                            sendToStatist('editor.format.tap', {formatType: 'bold', hotkey: true});
                            return false;
                        },
                        'Mod-i': () => {
                            sendToStatist('editor.format.tap', {formatType: 'italic', hotkey: true});
                            return false;
                        },
                        'Mod-Shift-x': () => {
                            this.editor.commands.toggleMark('strike');
                            sendToStatist('editor.format.tap', {formatType: 'strikethrough', hotkey: true});
                            return false;
                        },
                        'Mod-Shift-7': () => {
                            sendToStatist('editor.format.tap', {formatType: 'list_ordered', hotkey: true});
                            return false;
                        },
                        'Mod-Shift-8': () => {
                            sendToStatist('editor.format.tap', {formatType: 'list_bulleted', hotkey: true});
                            return false;
                        },
                        'Mod-Shift-9': () => {
                            sendToStatist('editor.format.tap', {formatType: 'quote', hotkey: true});
                            return false;
                        },
                        'Mod-Alt-Shift-c': () => {
                            sendToStatist('editor.format.tap', {formatType: 'code_block', hotkey: true});
                            return false;
                        },
                        'Shift-ArrowUp': () => {
                            return Boolean(onReplyLastPost?.());
                        },
                        'Shift-Mod-\\': () => {
                            return Boolean(onReactLastPost?.());
                        },
                        // eslint-disable-next-line @typescript-eslint/naming-convention
                        Enter: (state, __, ___, event) => {
                            const isIntoCodeBlock = state.selection.$from.parent.type.name === TimeWebkitCodeBlockExtension.name;
                            return !(ctrlSend || (codeBlockOnCtrlEnter && isIntoCodeBlock)) && Boolean(onEnterPress?.(event as unknown as React.KeyboardEvent<Element>));
                        },
                        // eslint-disable-next-line @typescript-eslint/naming-convention
                        ArrowUp: (_, __, ___, event) => {
                            return Boolean(onArrowUpPress?.(event as unknown as React.KeyboardEvent<Element>));
                        },
                        // eslint-disable-next-line @typescript-eslint/naming-convention
                        'Mod-Enter': (state, __, ___, event) => {
                            const isIntoCodeBlock = state.selection.$from.parent.type.name === TimeWebkitCodeBlockExtension.name;
                            return Boolean(ctrlSend || (codeBlockOnCtrlEnter && isIntoCodeBlock)) && Boolean(onEnterPress?.(event as unknown as React.KeyboardEvent<Element>));
                        },
                        Escape: () => {
                            onEscPress?.();
                            return true;
                        },
                    }),
                ];
            },
        }),
        TimeWebkitEditorPlaceholderPlugin.configure({
            placeholder,
        }),
        TimeWebkitMarkdownExtension,
        TimeWebkitHardBreakExtension,
        TimeWebkitCommandsExtension,
    ];

    if (hasMentions) {
        editorExtensions.push(
            MentionMark,
            TimeEditorMentionExtension.configure({
                channelId,
                teamId,
                predefinedMentions,
                withinThread,
                withinDirect,
                useGroupMentions: useCustomGroupMentions || useLDAPGroupMentions,
                disableInExtensions: disableMentionsInExtensions,
                disableInMarks: disableMentionsInMarks,
            }),
        );
    }

    editorExtensions.push(
        TimeEditorChannelMentionExtension.configure({
            teamId,
            predefinedChannels,
            withinThread,
        }),
    );

    editorExtensions.push(
        TimeEditorHashtagMentionExtension.configure({
            teamId,
            predefinedHashtags,
            withinThread,
        }),
    );

    editorExtensions.push(
        TimeEditorSlashCommandExtension.configure({
            teamId,
            channelId,
            rootId,
            withinThread,
            setServerError,
        }),
    );

    editorExtensions.push(
        TimeWebkitEmojiExtension.configure({withinThread}),
    );

    const editor = useEditor(
        {
            editorProps: {
                attributes: {
                    class: classNames(editorStyles.editor, className),
                    role: 'textbox',
                    id: 'post_textbox',
                    'data-testid': props.dataTestId,
                },
                handleKeyDown: (_, e) => {
                    if (!(e.altKey || e.shiftKey || e.ctrlKey || e.metaKey)) {
                        e.cancelBubble = true;
                    }
                },
                transformPastedHTML: (html) => {
                    return html
                        .replace(/.*<!--StartFragment-->/s, '')
                        .replace(/<!--EndFragment-->.*/s, '')
                        .replaceAll('\n', '<br/>');
                },
                transformPasted: (slice, view) => {
                    const transformMentions = (node: ProsemirrowNode) => {
                        const mentionMark = node.marks.find((mark) => mark.type.name === 'mention');
                        if (mentionMark && node.isText && node.text !== `@${mentionMark.attrs.id}`) {
                            return view.state.schema.text(`@${mentionMark.attrs.id}`, node.marks);
                        }

                        return node;
                    };

                    const newSlice = mapSlice(slice, transformMentions);

                    return newSlice;
                },
                clipboardTextParser: (text, $context, plainText, view) => {
                    const dom = document.createElement('div');

                    const html = parseHtmlFromMarkdown(text);
                    const nodes = getNodesFromHtmlString(html);

                    if (nodes) {
                        dom.append(...nodes);
                    }

                    return DOMParser.fromSchema(view.state.schema).parseSlice(dom, {
                        preserveWhitespace: true,
                        context: $context,
                    });
                },
                handlePaste: (view, clip) => {
                    const selection = view.state.tr.selection;
                    if (selection.from === selection.to) {
                        return false;
                    }

                    const selected = getSelectedText();
                    const urlRe = urlRegex();
                    if (selected && urlRe.test(selected)) {
                        const text = clip.clipboardData?.getData('text/plain');
                        if (!text) {
                            return false;
                        }

                        const marks = [];
                        const mark = view.state.schema.marks.link;
                        if (mark) {
                            const href = text.trim();
                            marks.push(mark.create({title: href, href}));
                        }
                        const node = view.state.schema.text(text, marks);
                        const tr = view.state.tr.replaceRange(
                            selection.from, selection.to, (new Slice(Fragment.from(node), 0, 0)));
                        view.dispatch(tr);
                        return true;
                    }

                    return false;
                },
            },
            onTransaction(props) {
                onCaretPositionChange?.(props.editor.state.selection.anchor || 0);
            },
            onUpdate(props) {
                onContentChange?.(toMarkdown(props.editor.state, props.editor.schema));
            },
            extensions: editorExtensions,
            enableCoreExtensions: false,
            parseOptions: {
                preserveWhitespace: 'full',
            },
        },
        [
            channelId,
            teamId,
            ctrlSend,
            codeBlockOnCtrlEnter,
            predefinedMentions,
            predefinedChannels,
            rootId,
            onReactLastPost,
            onReplyLastPost,
            onArrowUpPress,
            onEnterPress,
            onCaretPositionChange,
            onContentChange,
            useCustomGroupMentions,
            useLDAPGroupMentions,
        ],
    );

    const shouldRenderDraft = useRef(Boolean(draftMessage && editor?.isEmpty));

    useEffect(() => {
        if (!shouldRenderDraft.current) {
            return;
        }

        editor?.chain().setMarkdown(draftMessage, false).focus().run();

        shouldRenderDraft.current = false;
    }, [draftMessage, editor]);

    useEffect(() => {
        setEditor?.(editor);
    }, [editor, setEditor]);

    useEffect(() => {
        editor?.setEditable(editable, false);
    }, [editor, editable]);

    // @TODO: убрать после вывода из фиче флага
    useLayoutEffect(() => {
        // eslint-disable-next-line no-underscore-dangle
        window.__EDITOR__ = editor;

        return () => {
            // eslint-disable-next-line no-underscore-dangle
            window.__EDITOR__ = null;
        };
    }, [editor]);

    return (
        <EditorContext.Provider
            value={{
                editor: editor || undefined,
                charactersCount,
                maxCharacters,
                features: {
                    codeBlock,
                    emoji,
                    mention,
                },
            }}
        >
            <Content
                bodySlot={fileAttachmentSlot}
                emojiPicker={emojiPicker}
                fileUploader={fileUploader}
                actionsSlot={sendMenuSlot}
            />
        </EditorContext.Provider>
    );
};
